import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { AttentionItem } from "./components/NeedsAttention";

/**
 * Open payment exceptions (0093) — the two money failures that previously
 * reached nobody: a dead-lettered fulfillment job (the payment settled and the
 * booking may never have confirmed) and a `capacity_taken` reservation (the
 * client paid and the slot was given away).
 *
 * No hub filter here on purpose: RLS scopes a Hub Manager to their own hub's
 * bookings, same as `useOpsBookings`. Asking for all rows returns their own.
 */
export interface PaymentExceptionRow {
  id: string;
  kind: "dead_letter" | "capacity_taken" | "unmatched_payment" | "refused_delivery";
  booking_id: string | null;
  payment_id: string | null;
  created_at: string;
  detail: Record<string, unknown> | null;
}

export function usePaymentExceptions() {
  return useQuery({
    queryKey: ["payment-exceptions"],
    queryFn: async (): Promise<PaymentExceptionRow[]> => {
      const { data, error } = await supabase
        .from("payment_exceptions")
        .select("id, kind, booking_id, payment_id, created_at, detail")
        .is("resolved_at", null)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as PaymentExceptionRow[];
    },
    // Money incidents should not wait for a page reload to appear.
    refetchInterval: 60_000,
  });
}

/**
 * Copy states the FACT and what it implies for the money — never the remedy.
 * Whether a `capacity_taken` booking is refunded, rebooked, or overridden is
 * an Open Item (OQ-15/OQ-16); a queue row that says "refund this" would be
 * inventing the authority to do it (never-invent-policy.md).
 */
export const KIND_TEXT: Record<PaymentExceptionRow["kind"], string> = {
  dead_letter:
    "Payment settled but fulfillment failed after 8 attempts — the booking may never have confirmed.",
  capacity_taken:
    "Client paid, but the slot was taken before the payment confirmed. Booking is not reserved.",
  unmatched_payment:
    "A verified payment arrived that matches no booking of ours. Money may have moved.",
  refused_delivery:
    "A verified payment arrived and our own cross-check declined it, so nothing settled. The client may have paid for a booking still showing unpaid.",
};

export function toAttentionItems(rows: PaymentExceptionRow[]): AttentionItem[] {
  return rows.map((row) => {
    const ref = typeof row.detail?.booking_ref === "string" ? row.detail.booking_ref : null;
    return {
      id: `payment-exception:${row.id}`,
      tag: "PAYMENT",
      // Every kind here means money moved and something did not follow it.
      // There is no "warning" tier of that.
      tone: "danger" as const,
      text: ref ? `${ref} — ${KIND_TEXT[row.kind]}` : KIND_TEXT[row.kind],
      at: row.created_at,
      href: row.booking_id ? "/bookings" : undefined,
    };
  });
}

/** The same rows in the shape the shared attention engine reads
 * (`buildLiveAttention`) — one mapping for the live map and the dashboard. */
export function toPaymentAttentionInputs(rows: readonly PaymentExceptionRow[]) {
  return rows.map((row) => ({
    id: row.id,
    kind: row.kind,
    booking_id: row.booking_id,
    created_at: row.created_at,
    text: KIND_TEXT[row.kind],
    ref: typeof row.detail?.booking_ref === "string" ? row.detail.booking_ref : null,
  }));
}
